import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Input, Select, message, Space, Popconfirm, Tag, Card, Row, Col, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { doctorService } from '../../services';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import './Secretaries.css';

const Secretaries = () => {
    const [secretaries, setSecretaries] = useState([]);
    const [filteredSecretaries, setFilteredSecretaries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSecretary, setEditingSecretary] = useState(null);
    const [form] = Form.useForm();
    const { t } = useTranslation();

    useEffect(() => {
        fetchSecretaries();
    }, []);

    const fetchSecretaries = async () => {
        setLoading(true);
        try {
            const response = await doctorService.getSecretaries();
            setSecretaries(response.data);
            setFilteredSecretaries(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        let filtered = secretaries;

        if (values.name) {
            filtered = filtered.filter(s =>
                s.name.toLowerCase().includes(values.name.toLowerCase())
            );
        }

        if (values.phoneNumber) {
            filtered = filtered.filter(s =>
                s.phoneNumber.includes(values.phoneNumber)
            );
        }

        if (values.isActive !== undefined) {
            filtered = filtered.filter(s => s.isActive === (values.isActive === 'true'));
        }

        setFilteredSecretaries(filtered);
    };

    const handleReset = () => {
        setFilteredSecretaries(secretaries);
    };

    const handleCreate = () => {
        setEditingSecretary(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingSecretary(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await doctorService.deleteSecretary(id);
            message.success(t('success'));
            fetchSecretaries();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingSecretary) {
                await doctorService.updateSecretary(editingSecretary._id, values);
            } else {
                await doctorService.createSecretary(values);
            }
            message.success(t('success'));
            setModalVisible(false);
            form.resetFields();
            fetchSecretaries();
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        }
    };

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('email'),
            dataIndex: 'email',
            key: 'email',
            render: (email) => email || '-'
        },
        {
            title: t('language'),
            dataIndex: 'language',
            key: 'language',
            render: (lang) => lang === 'en' ? t('english') : lang === 'ar' ? t('arabic') : t('kurdish')
        },
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? t('active') : t('inactive')}
                </Tag>
            )
        },
        {
            title: t('createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('YYYY-MM-DD')
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title={t('confirmDelete')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button icon={<DeleteOutlined />} danger size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} sm={16} md={12}>
                    <h1 className="page-title">{t('secretaries')}</h1>
                </Col>
                <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        size="large"
                    >
                        {t('createSecretary')}
                    </Button>
                </Col>
            </Row>

            <Card className="search-filter-card">
                <SearchFilter
                    searchFields={[
                        { key: 'name', label: t('name'), placeholder: t('searchBy') + ' ' + t('name') },
                        { key: 'phoneNumber', label: t('phoneNumber'), placeholder: t('searchBy') + ' ' + t('phoneNumber') }
                    ]}
                    filterFields={[
                        {
                            key: 'isActive',
                            label: t('status'),
                            options: [
                                { label: t('active'), value: 'true' },
                                { label: t('inactive'), value: 'false' }
                            ]
                        }
                    ]}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filteredSecretaries}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                />
            </Card>

            <FormModal
                title={editingSecretary ? t('edit') + ' ' + t('secretaries') : t('createSecretary')}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onSubmit={handleSubmit}
                form={form}
                width={600}
                className="form-modal-two-column"
            >
                <Form.Item
                    name="name"
                    label={t('name')}
                    rules={[{ required: true, message: t('name') + ' ' + t('required') }]}
                >
                    <Input placeholder={t('name')} />
                </Form.Item>

                <Form.Item
                    name="phoneNumber"
                    label={t('phoneNumber')}
                    rules={[{ required: true, message: t('phoneNumber') + ' ' + t('required') }]}
                >
                    <Input disabled={!!editingSecretary} placeholder={t('phoneNumber')} />
                </Form.Item>

                {!editingSecretary && (
                    <Form.Item
                        name="password"
                        label={t('password')}
                        rules={[{ required: true, message: t('password') + ' ' + t('required') }]}
                    >
                        <Input.Password placeholder={t('password')} />
                    </Form.Item>
                )}

                <Form.Item name="email" label={t('email')}>
                    <Input type="email" placeholder={t('email')} />
                </Form.Item>

                <Form.Item
                    name="language"
                    label={t('language')}
                    rules={[{ required: true, message: t('language') + ' ' + t('required') }]}
                >
                    <Select placeholder={t('select')}>
                        <Select.Option value="en">{t('english')}</Select.Option>
                        <Select.Option value="ar">{t('arabic')}</Select.Option>
                        <Select.Option value="ku">{t('kurdish')}</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item name="address" label={t('address')} className="ant-form-item-full">
                    <Input.TextArea placeholder={t('address')} rows={2} />
                </Form.Item>

                {editingSecretary && (
                    <Form.Item name="isActive" label={t('status')} className="ant-form-item-full">
                        <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
                    </Form.Item>
                )}
            </FormModal>
        </div>
    );
};

export default Secretaries;
